import PartIcon from "@shared/svg/part.svg?react";
import {
  ExternalThumbnailFrame,
  isExternalVisibility,
} from "@shared/ui/Indicators";
import { MakulaIcon } from "@shared/ui/MakulaIcon";
import { MakulaImage } from "@shared/ui/MakulaImage";
import React from "react";

type ThumbnailSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";

interface PartThumbnailProps {
  /** Thumbnail URL from part (already resolved with fallbacks at service layer) */
  thumbnail?: string | null;
  /** Alt text for the image */
  alt?: string;
  /** Size of the thumbnail */
  size?: ThumbnailSize;
  /** Additional className */
  className?: string;
  /** Visibility scope from API */
  visibilityScope?: string | null;
}

const sizeClasses: Record<ThumbnailSize, { container: string; icon: string }> =
  {
    sm: { container: "w-xl h-xl", icon: "w-sm h-sm" },
    md: { container: "w-2xl h-2xl", icon: "w-md h-md" },
    lg: { container: "w-3xl h-3xl", icon: "w-lg h-lg" },
    xl: { container: "w-4xl h-4xl", icon: "w-xl h-xl" },
    "2xl": { container: "w-5xl h-5xl", icon: "w-2xl h-2xl" },
    "3xl": { container: "w-6xl h-6xl", icon: "w-3xl h-3xl" },
    "4xl": { container: "w-8xl h-8xl", icon: "w-4xl h-4xl" },
  };

export const PartThumbnail: React.FC<PartThumbnailProps> = ({
  thumbnail,
  alt = "Part",
  size = "xl",
  className = "",
  visibilityScope,
}) => {
  const { container, icon } = sizeClasses[size];
  const showExternal = isExternalVisibility(visibilityScope);

  const partFallback = (
    <div className="w-full h-full bg-brand-lightest flex items-center justify-center rounded-lg">
      <MakulaIcon icon={<PartIcon />} className={icon} color="#0517F8" />
    </div>
  );

  return (
    <ExternalThumbnailFrame
      showExternal={showExternal}
      className={`${container} ${className}`}
    >
      <MakulaImage
        src={thumbnail}
        alt={alt}
        fallback={partFallback}
        fit="contain"
        rounded="rounded-lg"
        className="w-full h-full"
      />
    </ExternalThumbnailFrame>
  );
};
